import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { useAuditLog } from "@/api/queries/audit";
import { useCurrentUser } from "@/api/queries/me";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";

export function AuditLogPage() {
  const { identity } = useAuth();
  if (!isRespaiAdmin(identity?.role)) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Audit log</h1>
        </header>
        <p className="text-sm text-destructive">
          The audit log is restricted to RespAI platform administrators.
        </p>
      </div>
    );
  }
  return <AuditLogPageInner />;
}

function AuditLogPageInner() {
  const me = useCurrentUser();
  const tz = me.data?.department?.timezone;
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const { data, isLoading, isError } = useAuditLog({
    actor: actor || undefined,
    action: action || undefined,
  });
  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Audit log</h1>
          <p className="text-sm text-muted-foreground">
            Every privileged action in this department.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Actor"
            aria-label="Filter by actor"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            className="w-40"
          />
          <Input
            placeholder="Action"
            aria-label="Filter by action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="w-40"
          />
        </div>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>
            {data ? `${data.total.toLocaleString()} entries` : "Loading…"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : isError ? (
            <p className="p-6 text-sm text-destructive">
              Failed to load the audit log. Check your connection and try again.
            </p>
          ) : data && data.items.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Timestamp</Th>
                  <Th>Actor</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                  <Th>IP</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.items.map((entry) => (
                  <Tr key={entry.id}>
                    <Td>{formatDateTime(entry.ts, tz)}</Td>
                    <Td>{entry.actor_display_name ?? entry.actor_user_id}</Td>
                    <Td className="font-mono text-xs">{entry.action}</Td>
                    <Td className="font-mono text-xs">
                      {entry.target_type
                        ? `${entry.target_type}/${entry.target_id ?? ""}`
                        : "—"}
                    </Td>
                    <Td>{entry.ip ?? "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              No audit entries.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
